import {
  BarChart3,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Download,
  FileCheck2,
  Users,
  XCircle,
} from "lucide-react";
import { useId, useMemo, useState, type CSSProperties } from "react";
import { Button, EmptyState, MetricCard, PageHeader } from "../components/ui";
import { downloadTextFile, formatDate, formatTime, statusLabel, todayISO } from "../lib/utils";
import { useApp } from "../state/AppContext";
import type {
  AttendanceMethod,
  AttendanceRecord,
  AttendanceStatus,
  SchoolClass,
  Student,
} from "../types";
import "../styles/reports.css";

type StatusCounts = Record<AttendanceStatus, number>;

interface ClassReport {
  schoolClass: SchoolClass;
  students: number;
  records: number;
  expected: number;
  coverage: number;
  attendanceRate: number;
  statuses: StatusCounts;
}

const emptyStatusCounts = (): StatusCounts => ({
  present: 0,
  late: 0,
  absent: 0,
  excused: 0,
});

const methodLabel: Record<AttendanceMethod, string> = {
  qr: "Imbasan QR",
  manual: "Rekod manual",
  admin: "Rekod admin",
};

const statusOrder: AttendanceStatus[] = ["present", "late", "absent", "excused"];

const statusTone: Record<AttendanceStatus, string> = {
  present: "var(--success)",
  late: "var(--accent)",
  absent: "var(--danger)",
  excused: "var(--info)",
};

const percent = (value: number, total: number) =>
  total > 0 ? Math.round((value / total) * 100) : 0;

const latestUniqueRecords = (records: AttendanceRecord[]) => {
  const latest = new Map<string, AttendanceRecord>();
  records.forEach((record) => {
    const key = `${record.date}|${record.studentId}`;
    const current = latest.get(key);
    if (
      !current ||
      new Date(record.timestamp).getTime() > new Date(current.timestamp).getTime()
    ) {
      latest.set(key, record);
    }
  });
  return Array.from(latest.values());
};

const csvCell = (value: string | number) =>
  `"${String(value).replaceAll('"', '""')}"`;

const shortDay = (date: string) =>
  new Intl.DateTimeFormat("ms-MY", { weekday: "short" })
    .format(new Date(`${date}T00:00:00`))
    .replace(".", "");

export function ReportsPage() {
  const { attendance, classes, students, settings } = useApp();
  const classFilterId = useId();
  const startDateId = useId();
  const endDateId = useId();

  const availableDates = useMemo(
    () => Array.from(new Set(attendance.map((record) => record.date))).sort(),
    [attendance],
  );
  const initialEnd = availableDates.at(-1) ?? todayISO();
  const initialStart =
    availableDates[Math.max(0, availableDates.length - 7)] ?? initialEnd;

  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  const [classId, setClassId] = useState("all");
  const [exportMessage, setExportMessage] = useState("");

  const studentById = useMemo(
    () => new Map(students.map((student) => [student.id, student])),
    [students],
  );

  const activeStudents = useMemo(
    () =>
      students.filter(
        (student) =>
          student.active && (classId === "all" || student.classId === classId),
      ),
    [classId, students],
  );

  const operatingDates = useMemo(
    () =>
      Array.from(
        new Set(
          attendance
            .filter((record) => record.date >= startDate && record.date <= endDate)
            .map((record) => record.date),
        ),
      ).sort(),
    [attendance, endDate, startDate],
  );

  const filteredRecords = useMemo(() => {
    const records = latestUniqueRecords(
      attendance.filter((record) => {
        const student = studentById.get(record.studentId);
        return (
          Boolean(student?.active) &&
          record.date >= startDate &&
          record.date <= endDate &&
          (classId === "all" || student?.classId === classId)
        );
      }),
    );
    return records.sort(
      (first, second) =>
        first.date.localeCompare(second.date) ||
        first.timestamp.localeCompare(second.timestamp),
    );
  }, [attendance, classId, endDate, startDate, studentById]);

  const summary = useMemo(() => {
    const statuses = filteredRecords.reduce<StatusCounts>((counts, record) => {
      counts[record.status] += 1;
      return counts;
    }, emptyStatusCounts());
    const attended = statuses.present + statuses.late;
    const expected = activeStudents.length * operatingDates.length;
    return {
      statuses,
      attended,
      expected,
      attendanceRate: percent(attended, filteredRecords.length),
      coverage: percent(filteredRecords.length, expected),
    };
  }, [activeStudents.length, filteredRecords, operatingDates.length]);

  const weeklyData = useMemo(
    () =>
      operatingDates.slice(-7).map((date) => {
        const records = filteredRecords.filter((record) => record.date === date);
        const attended = records.filter(
          (record) => record.status === "present" || record.status === "late",
        ).length;
        return {
          date,
          records: records.length,
          attended,
          rate: percent(attended, records.length),
        };
      }),
    [filteredRecords, operatingDates],
  );

  const classReports = useMemo<ClassReport[]>(() => {
    const visibleClasses =
      classId === "all"
        ? classes
        : classes.filter((schoolClass) => schoolClass.id === classId);

    return visibleClasses.map((schoolClass) => {
      const classStudents = students.filter(
        (student) => student.active && student.classId === schoolClass.id,
      );
      const records = filteredRecords.filter(
        (record) => studentById.get(record.studentId)?.classId === schoolClass.id,
      );
      const statuses = records.reduce<StatusCounts>((counts, record) => {
        counts[record.status] += 1;
        return counts;
      }, emptyStatusCounts());
      const expected = classStudents.length * operatingDates.length;
      return {
        schoolClass,
        students: classStudents.length,
        records: records.length,
        expected,
        coverage: percent(records.length, expected),
        attendanceRate: percent(statuses.present + statuses.late, records.length),
        statuses,
      };
    });
  }, [
    classId,
    classes,
    filteredRecords,
    operatingDates.length,
    studentById,
    students,
  ]);

  const selectedClassName =
    classId === "all"
      ? "Semua kelas"
      : classes.find((schoolClass) => schoolClass.id === classId)?.name ??
        "Kelas dipilih";

  const breakdownGradient =
    filteredRecords.length === 0
      ? "var(--surface-strong)"
      : (() => {
          let cursor = 0;
          const stops = statusOrder.map((status) => {
            const start = cursor;
            cursor += (summary.statuses[status] / filteredRecords.length) * 100;
            return `${statusTone[status]} ${start}% ${cursor}%`;
          });
          return `conic-gradient(${stops.join(", ")})`;
        })();

  const updateStartDate = (value: string) => {
    setStartDate(value);
    if (value > endDate) setEndDate(value);
    setExportMessage("");
  };

  const updateEndDate = (value: string) => {
    setEndDate(value);
    if (value < startDate) setStartDate(value);
    setExportMessage("");
  };

  const exportCsv = () => {
    const rows: Array<Array<string | number>> = [
      [
        "Tarikh",
        "Masa",
        "Kod murid",
        "Nama murid",
        "Kelas",
        "Status",
        "Kaedah",
        "Direkod oleh",
        "Catatan",
      ],
      ...filteredRecords.map((record) => {
        const student = studentById.get(record.studentId);
        return [
          record.date,
          formatTime(record.timestamp),
          student?.code ?? record.studentId,
          student?.name ?? "Murid tidak ditemui",
          student?.className ?? "—",
          statusLabel[record.status],
          methodLabel[record.method],
          record.recordedBy,
          record.note ?? "",
        ];
      }),
    ];
    const content = rows.map((row) => row.map(csvCell).join(",")).join("\n");
    const classSlug =
      classId === "all"
        ? "semua-kelas"
        : selectedClassName.toLowerCase().replace(/\s+/g, "-");
    downloadTextFile(
      `laporan-kehadiran-${classSlug}-${startDate}-${endDate}.csv`,
      content,
      "text/csv",
    );
    setExportMessage(
      `${filteredRecords.length} rekod telah dieksport untuk ${selectedClassName}.`,
    );
  };

  return (
    <main className="reports-page">
      <PageHeader
        eyebrow="Operasi sekolah"
        title="Laporan kehadiran"
        description="Pantau liputan rekod, pola kehadiran dan kelas yang memerlukan tindakan."
        actions={
          <Button
            icon={Download}
            onClick={exportCsv}
            disabled={filteredRecords.length === 0}
          >
            Eksport CSV
          </Button>
        }
      />

      <section className="reports-filter panel" aria-label="Tapisan laporan">
        <div className="reports-filter__heading">
          <span className="reports-filter__icon" aria-hidden="true">
            <BarChart3 size={19} />
          </span>
          <div>
            <strong>Skop laporan</strong>
            <span>Semua angka dikemas kini mengikut pilihan ini.</span>
          </div>
        </div>
        <div className="form-field">
          <label htmlFor={startDateId}>Tarikh mula</label>
          <input
            id={startDateId}
            type="date"
            value={startDate}
            max={todayISO()}
            onChange={(event) => updateStartDate(event.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor={endDateId}>Tarikh akhir</label>
          <input
            id={endDateId}
            type="date"
            value={endDate}
            min={startDate}
            max={todayISO()}
            onChange={(event) => updateEndDate(event.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor={classFilterId}>Kelas</label>
          <select
            id={classFilterId}
            value={classId}
            onChange={(event) => {
              setClassId(event.target.value);
              setExportMessage("");
            }}
          >
            <option value="all">Semua kelas</option>
            {classes.map((schoolClass) => (
              <option key={schoolClass.id} value={schoolClass.id}>
                {schoolClass.name}
              </option>
            ))}
          </select>
        </div>
      </section>

      <div className="reports-export-feedback" aria-live="polite">
        {exportMessage}
      </div>

      <section className="reports-metrics" aria-label="Ringkasan laporan">
        <MetricCard
          label="Kadar hadir"
          value={`${summary.attendanceRate}%`}
          context={`${summary.attended} hadir termasuk lewat`}
          icon={CheckCircle2}
          tone="success"
        />
        <MetricCard
          label="Liputan rekod"
          value={`${summary.coverage}%`}
          context={`${filteredRecords.length} daripada ${summary.expected} jangkaan`}
          icon={ClipboardCheck}
          tone="primary"
        />
        <MetricCard
          label="Rekod lewat"
          value={summary.statuses.late}
          context={`${percent(summary.statuses.late, filteredRecords.length)}% daripada rekod`}
          icon={Clock3}
          tone="warning"
        />
        <MetricCard
          label="Tidak hadir"
          value={summary.statuses.absent}
          context={`${percent(summary.statuses.absent, filteredRecords.length)}% daripada rekod`}
          icon={XCircle}
          tone="danger"
        />
      </section>

      <div className="reports-insights">
        <section className="reports-chart panel" aria-labelledby="reports-weekly-chart-title">
          <header className="reports-panel-heading">
            <div>
              <span>7 hari berrekod terakhir</span>
              <h2 id="reports-weekly-chart-title">Trend kehadiran mingguan</h2>
            </div>
            <strong>{summary.attendanceRate}% purata</strong>
          </header>
          {weeklyData.length ? (
            <figure
              className="reports-weekly-chart"
              aria-label={`Carta bar kadar kehadiran ${weeklyData
                .map((day) => `${formatDate(day.date)} ${day.rate}%`)
                .join(", ")}`}
            >
              <div className="reports-weekly-chart__scale" aria-hidden="true">
                <span>100%</span>
                <span>75%</span>
                <span>50%</span>
                <span>25%</span>
                <span>0%</span>
              </div>
              <ol className="reports-weekly-chart__plot">
                {weeklyData.map((day) => (
                  <li key={day.date}>
                    <span className="reports-weekly-chart__value" aria-hidden="true">
                      {day.rate}%
                    </span>
                    <span
                      className="reports-weekly-chart__bar"
                      style={
                        { "--report-bar-height": `${Math.max(day.rate, 3)}%` } as CSSProperties
                      }
                      aria-hidden="true"
                    />
                    <span className="reports-weekly-chart__day" aria-hidden="true">
                      <strong>{shortDay(day.date)}</strong>
                      <small>{new Date(`${day.date}T00:00:00`).getDate()}</small>
                    </span>
                    <span className="sr-only">
                      {formatDate(day.date)}: {day.attended} daripada {day.records} rekod
                      dikira hadir, bersamaan {day.rate}%.
                    </span>
                  </li>
                ))}
              </ol>
              <figcaption>
                Kadar hadir menggabungkan status Hadir dan Lewat bagi skop semasa.
              </figcaption>
            </figure>
          ) : (
            <EmptyState
              icon={BarChart3}
              title="Tiada trend untuk dipaparkan"
              message="Pilih julat tarikh yang mempunyai rekod kehadiran."
            />
          )}
        </section>

        <section className="reports-breakdown panel" aria-labelledby="breakdown-title">
          <header className="reports-panel-heading">
            <div>
              <span>Komposisi rekod</span>
              <h2 id="breakdown-title">Pecahan status</h2>
            </div>
            <strong>{filteredRecords.length} rekod</strong>
          </header>
          <div className="reports-breakdown__body">
            <div
              className="reports-donut"
              role="img"
              aria-label={statusOrder
                .map(
                  (status) =>
                    `${statusLabel[status]} ${summary.statuses[status]}, ${percent(
                      summary.statuses[status],
                      filteredRecords.length,
                    )}%`,
                )
                .join("; ")}
              style={{ background: breakdownGradient }}
            >
              <span aria-hidden="true">
                <strong>{filteredRecords.length}</strong>
                rekod
              </span>
            </div>
            <ul className="reports-legend">
              {statusOrder.map((status) => (
                <li key={status}>
                  <span
                    className={`reports-legend__dot reports-legend__dot--${status}`}
                    aria-hidden="true"
                  />
                  <span>{statusLabel[status]}</span>
                  <strong>{summary.statuses[status]}</strong>
                  <small>
                    {percent(summary.statuses[status], filteredRecords.length)}%
                  </small>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      <section className="reports-classes panel" aria-labelledby="class-report-title">
        <header className="reports-panel-heading reports-panel-heading--classes">
          <div>
            <span>Perbandingan operasi</span>
            <h2 id="class-report-title">Prestasi mengikut kelas</h2>
          </div>
          <p>
            {formatDate(startDate)} – {formatDate(endDate)} · {selectedClassName}
          </p>
        </header>

        {classReports.length ? (
          <>
            <div className="table-wrap desktop-only">
              <table className="data-table reports-class-table">
                <caption className="sr-only">
                  Perbandingan kehadiran setiap kelas bagi skop laporan
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Kelas</th>
                    <th scope="col">Murid aktif</th>
                    <th scope="col">Liputan</th>
                    <th scope="col">Hadir</th>
                    <th scope="col">Lewat</th>
                    <th scope="col">Tidak hadir</th>
                    <th scope="col">Berizin</th>
                    <th scope="col">Kadar hadir</th>
                  </tr>
                </thead>
                <tbody>
                  {classReports.map((report) => (
                    <tr key={report.schoolClass.id}>
                      <td>
                        <div className="reports-class-name">
                          <strong>{report.schoolClass.name}</strong>
                          <span>{report.schoolClass.teacher}</span>
                        </div>
                      </td>
                      <td className="tabular">{report.students}</td>
                      <td>
                        <span className="reports-coverage-cell">
                          <span>
                            <i style={{ width: `${report.coverage}%` }} />
                          </span>
                          <strong>{report.coverage}%</strong>
                        </span>
                      </td>
                      <td className="tabular">{report.statuses.present}</td>
                      <td className="tabular">{report.statuses.late}</td>
                      <td className="tabular">{report.statuses.absent}</td>
                      <td className="tabular">{report.statuses.excused}</td>
                      <td>
                        <span
                          className={`reports-rate ${
                            report.attendanceRate < 90 ? "reports-rate--attention" : ""
                          }`}
                        >
                          {report.attendanceRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="reports-class-cards mobile-only">
              {classReports.map((report) => (
                <article className="reports-class-card" key={report.schoolClass.id}>
                  <header>
                    <div>
                      <strong>{report.schoolClass.name}</strong>
                      <span>{report.schoolClass.teacher}</span>
                    </div>
                    <span
                      className={`reports-rate ${
                        report.attendanceRate < 90 ? "reports-rate--attention" : ""
                      }`}
                    >
                      {report.attendanceRate}%
                    </span>
                  </header>
                  <div className="reports-class-card__coverage">
                    <span>
                      Liputan rekod · {report.records}/{report.expected}
                    </span>
                    <strong>{report.coverage}%</strong>
                    <div role="progressbar" aria-valuenow={report.coverage} aria-valuemin={0} aria-valuemax={100}>
                      <i style={{ width: `${report.coverage}%` }} />
                    </div>
                  </div>
                  <dl>
                    <div>
                      <dt>Hadir</dt>
                      <dd>{report.statuses.present}</dd>
                    </div>
                    <div>
                      <dt>Lewat</dt>
                      <dd>{report.statuses.late}</dd>
                    </div>
                    <div>
                      <dt>Tidak hadir</dt>
                      <dd>{report.statuses.absent}</dd>
                    </div>
                    <div>
                      <dt>Berizin</dt>
                      <dd>{report.statuses.excused}</dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>
          </>
        ) : (
          <EmptyState
            icon={Users}
            title="Tiada kelas dalam skop"
            message="Pilih kelas lain untuk melihat perbandingan."
          />
        )}
      </section>

      <footer className="reports-footnote">
        <FileCheck2 size={15} aria-hidden="true" />
        Data laporan dijana daripada rekod kehadiran JosephCare dan dikemas kini secara
        langsung.
      </footer>
    </main>
  );
}

